import { estimateVoteTransactionGas, type ExpectedVote, gatewayVoteTransaction } from "@/services";
import { useSimpleGasEstimation } from "./useSimpleGasEstimation";
import type { Address, WalletClient } from "viem";
import type { FarmWeights } from "@/components";
import { useTransactionFees } from "./useTransactionFees";
import { useTransactionExecution } from "./useTransactionExecution";
import { useAccount } from "wagmi";
import { TOKEN_ADDRESSES } from "@/config";
import { TOKEN_IDS, type TransactionHash } from "@/types";
import { useDialogPortal } from "../shared";

interface VoteTransactionProps {
    unwindingEpochs: number;
    liquidVotes: FarmWeights;
    illiquidVotes: FarmWeights;
}

function transformVotes(votes: FarmWeights): ExpectedVote[] {
    const transformedVotes: ExpectedVote[] = [];

    for (const [farm, weight] of Object.entries(votes)) {
        transformedVotes.push({ farm: farm as Address, weight: BigInt(weight * 10 ** 16) });
    }

    const sum = transformedVotes.reduce((acc, curr) => acc + curr.weight, 0n);

    if (sum !== BigInt(10 ** 18) || sum === 0n) {
        return [];
    }

    return transformedVotes.filter((vote) => vote.weight !== 0n);
}

export function useVoteTransaction({ unwindingEpochs, liquidVotes, illiquidVotes }: VoteTransactionProps) {
    const transformedLiquidVotes = transformVotes(liquidVotes);
    const transformedIlliquidVotes = transformVotes(illiquidVotes);

    const { openVoteSuccessDialog } = useDialogPortal();

    const { chain } = useAccount();

    const asset = TOKEN_ADDRESSES[chain?.id ?? 1][TOKEN_IDS.USDC];

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useSimpleGasEstimation({
        estimateGasFn: (walletClient: WalletClient) =>
            estimateVoteTransactionGas(
                asset as Address,
                unwindingEpochs,
                transformedLiquidVotes,
                transformedIlliquidVotes,
                walletClient,
            ),
    });

    const { gasPriceUSD } = useTransactionFees({ estimatedGas: gasEstimate });

    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeVoteTransaction,
    } = useTransactionExecution({
        executeTransactionFn: async (walletClient) => {
            const voteTx = await gatewayVoteTransaction(
                asset as Address,
                unwindingEpochs,
                transformedLiquidVotes,
                transformedIlliquidVotes,
                walletClient,
            );

            if (!voteTx) {
                throw new Error("Vote transaction failed");
            }

            return voteTx;
        },
        onSuccess: (hash: TransactionHash) => {
            openVoteSuccessDialog({ transactionHash: hash });
            // closeDialog();
        },
        errorMessage: "Failed to vote",
    });

    return {
        gasPriceUSD,
        transactionHash,
        isExecuting,
        transactionError,
        gasError,
        isEstimatingGas,
        transformedLiquidVotes,
        transformedIlliquidVotes,
        executeVoteTransaction,
    };
}
